import { Button } from '@/components/ui/button'
import { Link, useNavigate } from 'react-router'
import { useEffect, useState } from 'react'
import Header from '@/components/Header'
import googleIcon from '@/assets/Landing/google.svg'
import authBg from '@/assets/Landing/authbg.svg'
import { useAuth } from '@/context/AuthContext'
import { useLocation } from 'react-router'
import { getRedirectPath, isPathAllowedForRole } from '@/utils/auth-redirect'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [pin, setPin] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [password, setPassword] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [step, setStep] = useState<'request' | 'verify'>('request')
  const [loginMethod, setLoginMethod] = useState<'pin' | 'password' | 'email-password'>('pin')
  const [emailForPassword, setEmailForPassword] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { sendLoginPin, verifyLoginPin, loginWithPassword, loginWithEmailPassword, user, loading } =
    useAuth()
  const redirectTo = (location.state as { redirectTo?: string })?.redirectTo

  useEffect(() => {
    if (user) {
      const defaultPath = getRedirectPath(user)
      // Only use redirectTo if it's allowed for the user's role
      const targetPath = redirectTo && isPathAllowedForRole(redirectTo, user.role)
        ? redirectTo
        : defaultPath
      navigate(targetPath, { replace: true })
    }
  }, [user, navigate, redirectTo])

  useEffect(() => {
    if (countdown <= 0) return
    const timer = setTimeout(() => setCountdown(prev => Math.max(prev - 1, 0)), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  const handleSendPin = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setError(null)
    try {
      await sendLoginPin(email)
      setMessage('Login PIN sent. Check your email and enter the 4-digit code.')
      setCountdown(60)
      setStep('verify')
    } catch (err) {
      setError((err as Error)?.message || 'Unable to send login PIN')
    }
  }

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setError(null)
    try {
      await verifyLoginPin({ email, pin })
      // Navigation will be handled by the useEffect watching user state
    } catch (err) {
      setError((err as Error)?.message || 'Invalid or expired PIN')
    }
  }

  const validatePhoneNumber = (phone: string): boolean => {
    // Basic phone number validation: should start with + and contain only digits after that
    const phoneRegex = /^\+?[1-9]\d{1,14}$/
    return phoneRegex.test(phone.replace(/[\s-]/g, ''))
  }

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setError(null)
    setPhoneError('')

    if (!validatePhoneNumber(phoneNumber)) {
      setPhoneError('Please enter a valid phone number (e.g., +2348012345678)')
      return
    }

    try {
      await loginWithPassword({ phoneNumber, password })
      // Navigation will be handled by the useEffect watching user state
    } catch (err) {
      setError((err as Error)?.message || 'Invalid credentials')
    }
  }

  const handleEmailPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setError(null)

    try {
      await loginWithEmailPassword({ email: emailForPassword, password })
      // Navigation will be handled by the useEffect watching user state
    } catch (err) {
      setError((err as Error)?.message || 'Invalid credentials')
    }
  }

  const handleGoogleSignIn = () => {
    // Placeholder for federated auth if added later
    setMessage('Google sign-in is not configured yet.')
  }

  return (
    <div className="min-h-screen bg-white flex flex-col w-full">
      <Header />

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:grid lg:grid-rows-2 w-full">
        {/* Left Section - Form */}
        <div className="flex flex-col lg:flex-row items-center justify-center px-6 lg:px-16 py-12 lg:py-16 w-full">
          <div className="w-full space-y-8 flex flex-col lg:flex-row gap-8">
            <div className="space-y-3 flex flex-col justify-start items-start lg:w-1/2">
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900">
                Log In
              </h2>
              <div className="space-y-1">
                <p className="text-gray-900 font-medium text-start text-lg lg:text-xl xl:text-2xl">Welcome back!</p>
                <p className="text-gray-600 text-base lg:text-lg xl:text-xl text-start">Sign in with your credentials or email PIN</p>
              </div>
            </div>

           <div className='flex flex-row items-center justify-center w-full lg:w-1/2'>
             <form
              onSubmit={
                loginMethod === 'pin'
                  ? step === 'request'
                    ? handleSendPin
                    : handleVerifyPin
                  : loginMethod === 'password'
                  ? handlePasswordLogin
                  : handleEmailPasswordLogin
              }
              className="space-y-6 w-full max-w-md"
             >
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm text-start">
                  {error}
                </div>
              )}
              {message && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm text-start">
                  {message}
                </div>
              )}
              {/* <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('email-password')
                    setError(null)
                    setMessage(null)
                    setPhoneError('')
                  }}
                  className={`flex-1 px-4 py-2.5 text-sm font-semibold transition-colors ${
                    loginMethod === 'email-password' || loginMethod === 'password'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Login with Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('pin')
                    setStep('request')
                    setError(null)
                    setMessage(null)
                    setPhoneError('')
                  }}
                  className={`flex-1 px-4 py-2.5 text-sm font-semibold transition-colors ${
                    loginMethod === 'pin'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Login with Email PIN
                </button>
              </div> */}

              {loginMethod === 'pin' ? (
                /* Email PIN login flow */
                <>
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 text-start">
                      Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="user@example.com"
                        className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-300 outline-none text-gray-900 placeholder:text-gray-400"
                        required
                        disabled={loading || step === 'verify'}
                      />
                    </div>
                  </div>

                  {step === 'verify' && (
                    <div className="space-y-2">
                      <label htmlFor="pin" className="block text-sm font-medium text-gray-700 text-start">
                        Enter 4-digit PIN
                      </label>
                      <input
                        type="text"
                        id="pin"
                        name="pin"
                        inputMode="numeric"
                        maxLength={4}
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\\D/g, ''))}
                        placeholder="1234"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none text-gray-900 placeholder:text-gray-400"
                        required
                        disabled={loading}
                      />
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>PIN expires in 10 minutes</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            if (countdown === 0) {
                              void handleSendPin(e)
                            }
                          }}
                          disabled={countdown > 0 || loading}
                          className="text-orange-500 font-semibold disabled:text-gray-400"
                        >
                          {countdown > 0 ? `Resend in 00:${countdown.toString().padStart(2, '0')}` : 'Resend PIN'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Password login flow (email or phone) */
                <>
                  {loginMethod === 'email-password' ? (
                    <div className="space-y-2">
                      <label htmlFor="emailForPassword" className="block text-sm font-medium text-gray-700 text-start">
                        Email
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input
                          type="email"
                          id="emailForPassword"
                          name="emailForPassword"
                          value={emailForPassword}
                          onChange={(e) => setEmailForPassword(e.target.value)}
                          placeholder="user@example.com"
                          className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-300 outline-none text-gray-900 placeholder:text-gray-400"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 text-start">
                        Phone Number
                      </label>
                      <PhoneNumberInput
                        id="phoneNumber"
                        name="phoneNumber"
                        value={phoneNumber}
                        onChange={(value) => {
                          setPhoneNumber(value)
                          setPhoneError('')
                        }}
                        placeholder="Phone number"
                        required
                        disabled={loading}
                        containerClassName={`px-4 py-3 ${phoneError ? 'border-red-500' : 'border-gray-300'}`}
                        inputClassName="py-0"
                      />
                      {phoneError && (
                        <p className="text-xs text-red-600 text-start">{phoneError}</p>
                      )}
                    </div>
                  )}
                  <div className="space-y-2">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 text-start">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 outline-none text-gray-900 placeholder:text-gray-400"
                      required
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod(loginMethod === 'email-password' ? 'password' : 'email-password')
                      setError(null)
                      setPhoneError('')
                    }}
                    className="text-sm text-orange-500 hover:text-orange-600 font-medium text-start"
                  >
                    {loginMethod === 'email-password' ? 'Use phone number instead' : 'Use email instead'}
                  </button>
                </>
              )}

              {/* Log In Button */}
              <Button
                type="submit"
                disabled={
                  !acceptedTerms ||
                  loading ||
                  (loginMethod === 'pin'
                    ? (step === 'verify' && pin.length !== 4)
                    : loginMethod === 'email-password'
                    ? !emailForPassword || !password
                    : !phoneNumber || !password)
                }
                className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-lg py-3 text-base font-medium transition-all disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loginMethod === 'pin'
                  ? step === 'request'
                    ? 'Send Login PIN'
                    : 'Verify & Log In'
                  : 'Log In'}
              </Button>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  disabled={loading}
                />
                <label htmlFor="terms" className="text-sm text-gray-600">
                  Accept{' '}
                  <Link to="/terms" className="text-orange-500 hover:text-orange-600">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-orange-500 hover:text-orange-600">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              {/* Divider */}
              {/* <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">Or</span>
                </div>
              </div> */}

              {/* Google Sign In */}
              {/* <Button
                type="button"
                onClick={handleGoogleSignIn}
                variant="outline"
                className="w-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg py-3 text-base font-medium flex items-center justify-center gap-3"
                disabled={loading}
              >
                <img src={googleIcon} alt="Google" className="w-5 h-5" />
                Sign in with Google
              </Button> */}
            </form>
           </div>
          </div>
        </div>

        {/* Right Section - Illustration */}
        <div className="lg:flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100/50 relative overflow-hidden rounded-tr-[32px] rounded-tl-[32px] mx-2 sm:mx-4 lg:mx-20 h-48 sm:h-64 lg:h-auto min-h-[200px] sm:min-h-[300px] lg:min-h-[400px]">
          <img 
            src={authBg} 
            alt="Authentication illustration" 
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
      </div>
    </div>
  )
}
